import {inject, Injectable} from '@angular/core';
import {createEffect, Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import * as DigitalBoardActions from './digital-board.actions';
import {DigitalBoardService} from './digital-board.service';
import {map, switchMap, catchError, tap} from 'rxjs/operators';
import {of, Subject} from 'rxjs';
import {Socket} from 'ngx-socket-io';
import {SocketRole} from 'src/app/models/SocketRoles';
import * as moment from 'moment';

@Injectable()
export class DigitalBoardEffects {
	private socket = inject(Socket);
	private destroy$ = new Subject<void>();

	// Setup socket connection (non-dispatching effect)
	setupSocketConnection$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DigitalBoardActions.loadDigitalBoardData),
				tap(({venueId}) => {
					this.setupSocketConnection(venueId);
				})
			),
		{dispatch: false}
	);

	// Load board data from API
	loadBoardData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DigitalBoardActions.loadDigitalBoardData),
			switchMap(({venueId}) => {
				return this.digitalBoardService.getBoardData(venueId).pipe(
					map(([orders, orderPools]) => {
						const today = moment().startOf('day');

						// Filter orders: today only, exclude DONE and CANCELLED_*
						const filteredOrders = orders.filter(
							order =>
								(order.asap || moment(order.orderAt).isSame(today, 'day')) &&
								order.status !== 'done' &&
								order.status !== 'cancelled_by_waiter' &&
								order.status !== 'cancelled_by_customer'
						);

						// Filter order pools: today only, exclude DONE and CANCELLED_*
						const filteredOrderPools = orderPools.filter(
							pool =>
								moment(pool.orderDay).isSame(today, 'day') &&
								pool.orderStatus !== 'done' &&
								pool.orderStatus !== 'cancelled_by_waiter' &&
								pool.orderStatus !== 'cancelled_by_customer'
						);

						// Separate into in preparation and ready
						const inPreparationOrders = [
							...filteredOrders.filter(
								order => order.status === 'in_preparation'
							),
							...filteredOrderPools.filter(
								pool => pool.orderStatus === 'in_preparation'
							)
						];

						const readyOrders = [
							...filteredOrders.filter(order => order.status === 'ready'),
							...filteredOrderPools.filter(pool => pool.orderStatus === 'ready')
						];

						return DigitalBoardActions.loadDigitalBoardDataSuccess({
							inPreparationOrders,
							readyOrders
						});
					}),
					catchError(error => {
						return of(
							DigitalBoardActions.loadDigitalBoardDataFailure({
								error: error.message || 'Failed to load board data'
							})
						);
					})
				);
			})
		)
	);

	private setupSocketConnection(venueId: string): void {
		// Remove any existing listeners
		this.socket.removeAllListeners();

		// Handle socket connection and authentication
		this.socket.on('connect', () => {
			this.authenticateSocket(venueId);
		});

		// Handle socket disconnection
		this.socket.on('disconnect', () => {
			console.log('[Digital Board] Socket disconnected');
		});

		// Re-authenticate on reconnection
		this.socket.on('reconnect', () => {
			console.log('[Digital Board] Socket reconnected, re-authenticating...');
			this.authenticateSocket(venueId);
		});

		// Listen for order updates
		this.socket.on(`venue-${venueId}-orders`, (data: any) => {
			if (data?.order && data?.type === 'update') {
				console.log('[Digital Board] Order update received:', data.order);
				this.store.dispatch(
					DigitalBoardActions.updateBoardOrder({order: data.order})
				);
			}
		});

		// Listen for order pool updates
		this.socket.on(`venue-${venueId}-order-pools`, (data: any) => {
			if (data?.orderPool && data?.type === 'updated') {
				console.log(
					'[Digital Board] Order pool update received:',
					data.orderPool
				);
				this.store.dispatch(
					DigitalBoardActions.updateBoardOrderPool({orderPool: data.orderPool})
				);
			}
		});
	}

	private authenticateSocket(venueId: string): void {
		this.socket.emit(
			'authenticate',
			[SocketRole.COUNTER_DISPLAY, SocketRole.COUNTER_DISPLAY_MAIN],
			[venueId],
			(ack: any) => {
				console.log('[Digital Board] Socket authenticated:', ack);
			}
		);
	}

	constructor(
		private actions$: Actions,
		private digitalBoardService: DigitalBoardService,
		private store: Store
	) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.socket.removeAllListeners();
	}
}
